import { useEffect, useState } from 'react';
import {
  Banner,
  Button,
  Card,
  CheckBox,
  FormGroup,
  Icon,
  Input,
  toast
} from '@uniquegood/realworld-studio-design';
import { useParams } from 'react-router-dom';
import Uploader from '@/components/Uploader';
import ContactMailForm from '@/pages/settings/components/ContactMailForm';
import { CardItemGap, FlexContentCenter, Gap, MarginTop24, MarginTop8 } from '@/styles';
import DisableChannel from './DisableChannel';
import { coreApi } from '@/api';
import { useDebounce, useRequest } from '@/hooks';

const ChannelInfoCardHeaderItem = {
  content: '채널 정보',
  isRequired: true,
  prefix: <Icon icon="home_solid" size="20px" />
};

const NAME_MAX_LENGTH = 30;
const DESCRIPTION_MAX_LENGTH = 80;

interface ChannelProps {
  appName: string;
  appDescription: string;
}

async function checkAvailableChannelName(name: string) {
  return coreApi
    .get<boolean>('api/apps/checkName', {
      params: {
        name
      }
    })
    .then(({ data }) => data);
}

function Channel({ appName, appDescription }: ChannelProps) {
  const [isAvailableName, setIsAvailableName] = useState<boolean | 'validating'>(true);
  const [name, setName] = useState(appName);
  const debouncedName = useDebounce(name, 200);
  const [isUploaded, setIsUploaded] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UserFileResponseModel[]>();
  const [description, setDescription] = useState(appDescription);
  const [subscribeCommunityEmail, setSubscribeCommunityEmail] = useState(true);

  const { appId } = useParams<{ appId: string }>();

  const {
    data: app,
    mutate,
    isValidating
  } = useRequest<ApplicationResponseModel>(
    `/apps/${appId}`,
    {},
    {
      headers: {
        'x-rwd-api-version': '1.1'
      }
    }
  );

  useEffect(() => {
    if (!app) {
      return;
    }

    const uploadedFiles: UserFileResponseModel[] = !app.imageId
      ? []
      : [
          {
            id: app.imageId,
            url: app.imageUrl
          }
        ];

    setName(app.name ?? '');
    setDescription(app.description ?? '');
    setSubscribeCommunityEmail(!app.unsubscribeCommunityEmail);
    setUploadedFiles(uploadedFiles);
  }, [app]);

  useEffect(() => {
    async function checkAvailable() {
      if (app === undefined || !debouncedName) return;

      setIsAvailableName('validating');

      try {
        const isAvailableChannelName = await checkAvailableChannelName(name);
        setIsAvailableName(isAvailableChannelName || app.name === name);
      } catch {
        toast({ message: '중복 체크에 실패했습니다. 다시 시도해주세요!', type: 'error' });
      }
    }

    checkAvailable();
  }, [debouncedName]);

  const handleSave = async () => {
    if (!name || name.trim() === '') {
      toast({ message: '채널 이름이 비워져있어요', type: 'error' });
      return;
    }

    if (isAvailableName === 'validating') {
      toast({ message: '잠시 후 다시 시도해주세요', type: 'error' });
      return;
    }

    if (isAvailableName === false) {
      toast({ message: '이미 사용 중인 이름입니다', type: 'error' });
      return;
    }

    if (isUploaded) {
      toast({ message: '파일 업로드를 완료해주세요!', type: 'error' });
      return;
    }

    try {
      const bodyData = {
        name,
        description,
        imageId: uploadedFiles?.[0]?.id ?? '',
        channelType: app?.channelType ?? 'Normal',
        unsubscribeCommunityEmail: !subscribeCommunityEmail
      };
      await coreApi.put<CommonResponseModel<string>>(`/apps/${appId}`, bodyData);
      await mutate();
      toast({ message: '채널 정보가 저장되었습니다!' });
    } catch (e) {
      toast({ message: '채널 정보 저장에 실패했습니다.', type: 'error' });
    }
  };

  return (
    <>
      <Card header={ChannelInfoCardHeaderItem} cssStyle={CardItemGap}>
        <FormGroup
          id="name"
          label="채널 이름"
          errorText={isAvailableName === false ? '이미 사용 중인 이름입니다' : ''}
          helpText="게임을 공개할 채널의 이름을 설정해주세요"
          requiredIndicator
          cssStyle={Gap}
        >
          <Input
            id="name"
            error={isAvailableName === false}
            placeholder="예) 윤익월드"
            maxLength={NAME_MAX_LENGTH}
            onChange={setName}
            showCharacterCount
            value={name}
          />
        </FormGroup>
        <FormGroup label="채널 대표 이미지">
          <Uploader
            getUploadLoadingStatus={(status) => setIsUploaded(status)}
            getUploadFiles={(files) => setUploadedFiles(files)}
            uploadedFiles={uploadedFiles}
            size="small"
            ratio="square"
          />
        </FormGroup>
        <FormGroup
          id="description"
          label="채널 한 줄 소개"
          helpText="채널을 한 마디로 표현해주세요"
        >
          <Input
            id="description"
            placeholder="예) 내가 만든 게임으로 세상을 윤익하게!"
            maxLength={DESCRIPTION_MAX_LENGTH}
            onChange={setDescription}
            showCharacterCount
            value={description}
          />
        </FormGroup>
        <ContactMailForm css={MarginTop24} />
        <CheckBox
          onChange={(event) => setSubscribeCommunityEmail(event.target.checked)}
          checked={subscribeCommunityEmail}
          cssStyle={MarginTop8}
        >
          커뮤니티 업데이트 소식 받아보기
        </CheckBox>
        {!subscribeCommunityEmail && (
          <Banner
            header="커뮤니티 업데이트 소식을 받아보면 이런 점이 좋아요!"
            type="informational"
            cssStyle={MarginTop8}
          >
            <ol>
              <li>커뮤니티에 등록된 글을 놓치지 않게 알려줘요.</li>
              <li>플레이어의 피드백과 제안을 메일로 받아볼 수 있어요.</li>
            </ol>
          </Banner>
        )}
        <Button
          type="primary"
          size="large"
          cssStyle={[MarginTop24, FlexContentCenter]}
          onClick={handleSave}
          loading={isValidating}
        >
          변경사항 저장
        </Button>
      </Card>
      <div css={MarginTop24}>
        <DisableChannel name={appName} />
      </div>
    </>
  );
}

export default Channel;
